/** Newsletter volume over time, by year, month, weekday and country. */
import { useState } from 'react'
import { PageHeader } from '../App'
import { useApi } from '../lib/api'
import { TrendChart, BarsChart, MonthHeatmap } from '../components/charts'
import {
  Card, Tile, Tabs, Empty, QueryState, fmtInt, downloadCsv, AlumniSwitch,
} from '../components/ui'
import { useRelabel } from '../lib/terms'

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export default function ActivityReport() {
  const t = useRelabel()
  const [withAlumni, setWithAlumni] = useState(false)
  const query = useApi(
    ['activity', withAlumni],
    `stats/activity${withAlumni ? '?include_alumni=1' : ''}`
  )
  const [grain, setGrain] = useState('year')

  return (
    <PageHeader
      title="Activity over Time"
      sub="How the archive has filled up since 2007"
      actions={<AlumniSwitch checked={withAlumni} onChange={setWithAlumni} where="updates" />}
    >
      <QueryState query={query}>
        {(d) => {
          const total = d.byYear.reduce((a, r) => a + r.n, 0)
          const peak = d.byYear.slice().sort((a, b) => b.n - a.n)[0]
          const weekday = d.byWeekday.map((r) => ({
            day: WEEKDAYS[Number(r.weekday)] ?? r.weekday,
            n: r.n,
          }))

          // Pivot country x year into one row per year for the stacked chart.
          const topCountries = topN(d.byCountryYear, 6)
          const pivot = pivotByYear(d.byCountryYear, topCountries)

          return (
            <div className="col">
              <div className="grid grid-4">
                <Tile label="Updates" value={fmtInt(total)} />
                <Tile label="Years covered" value={fmtInt(d.byYear.length)} />
                <Tile label="Busiest year" value={peak ? peak.year : '—'} note={peak ? `${fmtInt(peak.n)} updates` : ''} />
                <Tile
                  label="Peak distinct writers"
                  value={fmtInt(Math.max(...d.byYear.map((r) => r.partners), 0))}
                  note="in a single year"
                />
              </div>

              <Card
                title="Volume"
                actions={
                  <>
                    <Tabs
                      active={grain}
                      onChange={setGrain}
                      tabs={[
                        { key: 'year', label: 'By year' },
                        { key: 'month', label: 'By month' },
                        { key: 'heat', label: 'Heatmap' },
                      ]}
                    />
                    <button
                      className="btn btn-sm"
                      onClick={() =>
                        downloadCsv('activity-by-year.csv', d.byYear, [
                          { key: 'year', label: 'Year' },
                          { key: 'n', label: 'Updates' },
                          { key: 'partners', label: 'Distinct partner units' },
                          { key: 'highlighted', label: 'Highlighted' },
                          { key: 'avg_words', label: 'Avg words' },
                        ])
                      }
                    >
                      ↓ CSV
                    </button>
                  </>
                }
              >
                {grain === 'year' && (
                  <BarsChart
                    data={d.byYear}
                    x="year"
                    series={[
                      { key: 'n', label: 'Updates' },
                      { key: 'partners', label: 'Distinct partner units' },
                    ]}
                    height={300}
                  />
                )}
                {grain === 'month' && (
                  <TrendChart
                    data={d.byMonth}
                    x="month"
                    series={[{ key: 'n', label: 'Updates' }]}
                    area
                    height={300}
                  />
                )}
                {grain === 'heat' && <MonthHeatmap byMonth={d.byMonth} />}
              </Card>

              <div className="grid grid-2">
                <Card title="Average Length by Year" sub="Words per update">
                  <TrendChart data={d.byYear} x="year" series={[{ key: 'avg_words', label: 'Avg words' }]} height={230} />
                </Card>
                <Card title="Highlighted Updates by Year">
                  <BarsChart data={d.byYear} x="year" series={[{ key: 'highlighted', label: 'Highlighted' }]} height={230} />
                </Card>
              </div>

              <div className="grid grid-2">
                <Card title="Which Day Updates Arrive On">
                  {weekday.length ? (
                    <BarsChart data={weekday} x="day" series={[{ key: 'n', label: 'Updates' }]} horizontal height={250} />
                  ) : (
                    <Empty title="No data" />
                  )}
                </Card>
                <Card title="Top Countries by Year" sub={`Six busiest countries, stacked`}>
                  {pivot.length ? (
                    <BarsChart
                      data={pivot}
                      x="year"
                      series={topCountries.map((c) => ({ key: c, label: c }))}
                      stacked
                      height={250}
                    />
                  ) : (
                    <Empty title="No data" />
                  )}
                </Card>
              </div>

              <Card title="By Year">
                <div className="table-wrap flush">
                  <table className="data compact">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th className="right">Updates</th>
                        <th className="right">{t('Distinct partner units')}</th>
                        <th className="right">Highlighted</th>
                        <th className="right">Avg words</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.byYear.map((r) => (
                        <tr key={r.year}>
                          <td className="mono">{r.year}</td>
                          <td className="num">{fmtInt(r.n)}</td>
                          <td className="num">{fmtInt(r.partners)}</td>
                          <td className="num">{fmtInt(r.highlighted)}</td>
                          <td className="num">{fmtInt(r.avg_words)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}

/** The n countries with the most newsletters overall. */
function topN(byCountryYear, n) {
  const totals = new Map()
  for (const r of byCountryYear) {
    totals.set(r.country_name, (totals.get(r.country_name) ?? 0) + r.n)
  }
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([name]) => name)
}

/** One row per year, one column per selected country. */
function pivotByYear(byCountryYear, countries) {
  const set = new Set(countries)
  const years = new Map()
  for (const r of byCountryYear) {
    if (!set.has(r.country_name)) continue
    if (!years.has(r.year)) years.set(r.year, { year: r.year })
    years.get(r.year)[r.country_name] = r.n
  }
  return [...years.values()].sort((a, b) => a.year.localeCompare(b.year))
}
